import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createBlock as _createBlock, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, withCtx as _withCtx } from "vue";
const _hoisted_1 = {
    "aria-live": "polite",
    "aria-atomic": "true",
    class: "sr-only"
};
const _hoisted_2 = { key: 0 };
const _hoisted_3 = { key: 1 };
const _hoisted_4 = { key: 2 };
const _hoisted_5 = {
    key: 1,
    class: "flex flex-col flex-1 min-h-0 px-5 pb-4"
};
const _hoisted_6 = { class: "flex flex-wrap gap-4 py-4 shrink-0" };
const _hoisted_7 = { class: "self-center overflow-y-auto me-auto" };
const _hoisted_8 = ["id", "aria-labelledby"];
import { Button, ButtonGroup, buttonGroupTabId, FormSaveButton, ImageUploadInput, NavHeader, UnsavedChangesModal, } from "@eudonet/opal";
import { computed, onMounted, onUnmounted, reactive, ref, toRef, useId, watch } from "vue";
import { handleNavigationFailure } from "@/shared/utils/navigation";
import { useRoute, useRouter, onBeforeRouteUpdate } from "vue-router";
import { useI18n } from "vue-i18n";
import { useEntitySave } from "@/shared/composables/useEntitySave";
import { useOrganisationStore } from "../../stores/useOrganisationStore";
import { useAppToast } from "@/shared/composables/useAppToast";
import { usePermissions, useListNavPager } from "@/shared/composables";
import { getRelationTypeBadgeTheme } from "../../domain/relation-type";
import { getOrganisationById, saveOrganisationById, searchOrganisations, SaveValidationError, } from "../../infrastructure/adapters/organisationService";
import { mapLayoutToOrganisation } from "../../infrastructure/mappers/mapLayoutToOrganisation";
import OrganisationTabDetails from "../components/organisation-detail/OrganisationTabDetails.vue";
import OrganisationContactsTab from "../components/organisation-detail/OrganisationContactsTab.vue";
import OrganisationDonationsTab from "../components/organisation-detail/OrganisationDonationsTab.vue";
import OrganisationSubscriptionsTab from "../components/organisation-detail/OrganisationSubscriptionsTab.vue";
import OrganisationDetailSkeleton from "../components/OrganisationDetailSkeleton.vue";
import OrganisationPropertiesModal from "../components/OrganisationPropertiesModal.vue";
import { useLogoState } from "../../composables/useLogoState";
export default /*@__PURE__*/ _defineComponent({
    __name: 'Organisation',
    setup(__props) {
        const { t } = useI18n();
        const route = useRoute();
        const router = useRouter();
        const organisationStore = useOrganisationStore();
        const { toastError, toastSuccess } = useAppToast();
        const { canEditOphub: canEdit, disabledTooltip } = usePermissions();
        const organisationId = computed(() => route.params.id);
        // Back link: reopen the creation modal (dedup "Accéder à la fiche" flow) instead of
        // dropping the user on the table view when the fiche was reached from CreateOrganisationModal
        // (OP-7384). The query is set only by that flow (see CreateOrganisationModal.goToOrganisation).
        const backLinkTo = computed(() => route.query.fromOrganisationCreate === "1"
            ? {
                name: "OrganisationList",
                query: { reopenCreateOrganisation: "1", legalName: route.query.legalName },
            }
            : { name: "OrganisationList" });
        const backLinkLabel = computed(() => route.query.fromOrganisationCreate === "1"
            ? t("organisation.create.backToCreate")
            : t("common.backToOverview"));
        const { isPreviousDisabled, isNextDisabled, isFetchingPrevious, isFetchingNext, goToPrevious, goToNext, } = useListNavPager("organisation", organisationId, searchOrganisations);
        const logoUploadRef = ref(null);
        const tabDetailsRef = ref(null);
        // ─── Form state (populated from entity on load) ───────────────────────────────
        function createEmptyState() {
            return {
                id: "",
                externalId: "",
                avatarUrl: null,
                status: "",
                createdAt: "",
                updatedAt: "",
                tags: [],
                addresses: [],
                mainInformation: {
                    legalName: "",
                    acronym: "",
                    tradeName: null,
                    organisationType: "",
                    relationTypes: [],
                    industrySector: "",
                    description: "",
                },
                administrativeInformation: {
                    legalForm: "",
                    establishmentNature: "",
                    registrationDate: null,
                    headcount: null,
                    employeeRange: null,
                    employeeRangeYear: null,
                    siret: "",
                    siretValidation: null,
                    activityCode: "",
                    activityCodeLabel: "",
                    vatNumber: "",
                    vatValidation: null,
                    turnover: null,
                    legalStatus: null,
                    parentOrganisationId: null,
                    parentOrganisationName: null,
                },
                contactInformation: {
                    phones: [],
                    emails: [],
                    domainName: null,
                    socialLinks: [],
                },
                selectedRelationTypes: [],
                properties: {
                    externalId: null,
                    status: null,
                    archivingReason: null,
                    geolocalisation: null,
                    createdAt: null,
                    updatedAt: null,
                    createdBy: null,
                    updatedBy: null,
                },
            };
        }
        const state = reactive(createEmptyState());
        const showUnsavedChangesModal = ref(false);
        const showPropertiesModal = ref(false);
        const srAnnouncement = ref("");
        const pendingTabKey = ref(null);
        const actionsMenuItems = computed(() => [
            {
                label: t("organisation.detail.actions.properties"),
                icon: "ri-information-line",
                onClick: () => (showPropertiesModal.value = true),
            },
        ]);
        // ─── Entity save composable (owns load, save, isSaving, isDirty) ──────────────
        const { entityLayout, originalGenericValues, currentEntity, isLoading, isSaving, error, loadEntity, save, clearEntity, isDirty, markAsSaved, resetToOriginal, setDirty, proceedNavigation, cancelNavigation, } = useEntitySave(getOrganisationById, mapLayoutToOrganisation, "organisation.detail.loadError", state, {
            onNavigationBlocked: (proceed, cancel) => {
                showUnsavedChangesModal.value = true;
                pendingRouteCallbacks.proceed = proceed;
                pendingRouteCallbacks.cancel = cancel;
            },
            enableBeforeUnload: true,
        });
        const pendingRouteCallbacks = {
            proceed: null,
            cancel: null,
        };
        const isFormDirty = computed(() => isDirty.value || (tabDetailsRef.value?.isAddressDirty ?? false));
        const hasClientValidationErrors = computed(() => tabDetailsRef.value?.hasClientValidationErrors ?? false);
        // Generic blocks live outside of state, so useDirtyState doesn't track them.
        // Watch separately and call setDirty once the initial load is done.
        const genericBlocksReady = ref(false);
        watch(() => entityLayout.value?.layout?.blocks
            .filter((b) => b.kind === "generic")
            .flatMap((b) => b.fields.map((f) => f.value)), () => {
            if (genericBlocksReady.value)
                setDirty(true);
        }, { deep: true });
        // ─── Logo ─────────────────────────────────────────────────────────────────────
        const { pendingLogoFile, logoRemoved, preModalLogoState, handleLogoUpload, handleLogoRemove, handleLogoCancel, resetLogoState } = useLogoState(toRef(state, "avatarUrl"));
        function fileToBase64(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });
        }
        // ─── Load ─────────────────────────────────────────────────────────────────────
        onMounted(() => {
            void Promise.all([
                loadEntity(organisationId.value),
                organisationStore.loadAddresses(organisationId.value),
            ]);
        });
        // Vue Router reuses this component instance when navigating between organisations
        // (same route, different params), so onMounted only fires once. Watch the id to
        // reload whenever the user navigates via the pager or a direct link.
        watch(organisationId, (newId, oldId) => {
            if (newId === oldId)
                return;
            genericBlocksReady.value = false;
            resetLogoState();
            organisationStore.clearAddresses();
            void Promise.all([
                loadEntity(newId),
                organisationStore.loadAddresses(newId),
            ]);
        });
        // onBeforeRouteLeave (registered by useEntitySave) does NOT fire for same-route
        // param changes — only onBeforeRouteUpdate does. Guard pager navigation here so
        // clicking next/previous while editing still triggers the UnsavedChangesModal.
        // We return a Promise so Vue Router waits for the user's decision; resolving with
        // `false` cancels the navigation, resolving with `undefined` lets it proceed.
        let pendingPagerNav = null;
        onBeforeRouteUpdate((to, from) => {
            if (to.params.id !== from.params.id && isFormDirty.value) {
                return new Promise((resolve) => {
                    showUnsavedChangesModal.value = true;
                    pendingPagerNav = { proceed: () => resolve(), cancel: () => resolve(false) };
                });
            }
        });
        onUnmounted(() => {
            if (state.avatarUrl?.startsWith("blob:"))
                URL.revokeObjectURL(state.avatarUrl);
            clearEntity();
            organisationStore.clearAddresses();
        });
        watch(error, (msg) => {
            if (msg)
                toastError(msg);
        });
        watch(currentEntity, (org) => {
            if (org) {
                hydrateState(org);
                markAsSaved();
                genericBlocksReady.value = true;
            }
        }, { immediate: true });
        function hydrateState(org) {
            Object.assign(state, {
                ...org,
                selectedRelationTypes: [...org.mainInformation.relationTypes],
            });
        }
        // ─── Save ─────────────────────────────────────────────────────────────────────
        const handleSave = async () => {
            if (!(tabDetailsRef.value?.validate() ?? true)) {
                // Surface the failure visibly, not only to screen readers: the exit-modal
                // Save can reach this before the field-level error state has toggled
                // hasClientValidationErrors, so without the toast a click looks like a no-op.
                srAnnouncement.value = t("errors.formInvalid");
                toastError("errors.formInvalid");
                return false;
            }
            srAnnouncement.value = "";
            try {
                const logo = pendingLogoFile.value ? await fileToBase64(pendingLogoFile.value) : undefined;
                const blobUrlToRevoke = state.avatarUrl?.startsWith("blob:") ? state.avatarUrl : null;
                // BFF UpdatedLayout never includes logoUrl so hydrateState always resets avatarUrl to null.
                // Snapshot non-blob URL and the removed flag before save so we can restore below.
                const avatarUrlSnapshot = blobUrlToRevoke ? null : state.avatarUrl;
                const wasLogoRemoved = logoRemoved.value;
                await save(organisationId.value, async () => {
                    return saveOrganisationById(organisationId.value, state, entityLayout.value, originalGenericValues.value, logo, logoRemoved.value || undefined);
                });
                if (blobUrlToRevoke)
                    URL.revokeObjectURL(blobUrlToRevoke);
                if (state.avatarUrl === null && !wasLogoRemoved) {
                    state.avatarUrl = pendingLogoFile.value
                        ? URL.createObjectURL(pendingLogoFile.value)
                        : avatarUrlSnapshot;
                }
                pendingLogoFile.value = null;
                logoRemoved.value = false;
                preModalLogoState.value = null;
                // hydrateState (via watch(currentEntity)) runs before this continuation and snapshots
                // avatarUrl as null — re-snapshot now that the URL is restored to avoid a false dirty.
                markAsSaved();
            }
            catch (err) {
                if (err instanceof SaveValidationError) {
                    tabDetailsRef.value?.setServerErrors(err.errors);
                }
                toastError("organisation.detail.saveError");
                srAnnouncement.value = t("organisation.detail.saveError");
                return false;
            }
            const pendingPrimary = tabDetailsRef.value?.pendingPrimaryAddress ?? null;
            if (pendingPrimary) {
                const isNew = !organisationStore.addresses.some((a) => a.id === pendingPrimary.id);
                try {
                    if (isNew) {
                        await organisationStore.addAddress(organisationId.value, pendingPrimary);
                    }
                    else {
                        await organisationStore.editAddress(organisationId.value, pendingPrimary);
                    }
                }
                catch {
                    const addressErrorKey = isNew
                        ? "organisation.detail.addresses.addError"
                        : "organisation.detail.addresses.editError";
                    // The organisation core save succeeded but the primary-address write did not: the save is
                    // partial, so only the error surfaces — a success toast here would tell the user everything
                    // was saved when it was not.
                    toastError(addressErrorKey);
                    srAnnouncement.value = t(addressErrorKey);
                    return false;
                }
            }
            toastSuccess("organisation.detail.saveSuccess");
            srAnnouncement.value = t("organisation.detail.saveSuccess");
            return true;
        };
        // ─── Layout headers ───────────────────────────────────────────────────────────
        const layoutHeaders = computed(() => ({
            "main-info": t("organisation.detail.mainInformation.title"),
            "communication": t("organisation.detail.communication.title"),
            "admin-info": t("organisation.detail.administrativeInformation.title"),
        }));
        // ─── Header badge ─────────────────────────────────────────────────────────────
        const relationTypeBadges = computed(() => state.selectedRelationTypes.map((relationType) => ({
            label: t(`organisation.detail.options.relationType.${relationType}`),
            theme: getRelationTypeBadgeTheme(relationType),
        })));
        // ─── Tabs ─────────────────────────────────────────────────────────────────────
        const tabs = computed(() => [
            { key: "details", label: t("organisation.detail.tabs.details") },
            { key: "contacts", label: t("organisation.detail.tabs.contacts") },
            { key: "donations", label: t("organisation.detail.tabs.donations") },
            { key: "subscriptions", label: t("organisation.detail.tabs.subscriptions") },
        ]);
        const activeTab = ref("details");
        const panelId = useId();
        // ─── Tab navigation ───────────────────────────────────────────────────────────
        const handleTabChange = (tabKey) => {
            if (tabKey === activeTab.value)
                return;
            if (isFormDirty.value) {
                pendingTabKey.value = tabKey;
                showUnsavedChangesModal.value = true;
            }
            else {
                activeTab.value = tabKey;
            }
        };
        const handleStay = () => {
            showUnsavedChangesModal.value = false;
            pendingTabKey.value = null;
            if (pendingPagerNav) {
                pendingPagerNav.cancel();
                pendingPagerNav = null;
            }
            else if (pendingRouteCallbacks.cancel) {
                cancelNavigation();
                pendingRouteCallbacks.proceed = null;
                pendingRouteCallbacks.cancel = null;
            }
        };
        // Resume whatever the guard interrupted — a tab switch, a pager move, or a real
        // route navigation. Shared by the discard path (after reverting) and the
        // save-and-continue path (after a successful persist).
        function proceedPendingNavigation() {
            if (pendingTabKey.value) {
                activeTab.value = pendingTabKey.value;
                pendingTabKey.value = null;
            }
            if (pendingPagerNav) {
                pendingPagerNav.proceed();
                pendingPagerNav = null;
            }
            else if (pendingRouteCallbacks.proceed) {
                proceedNavigation();
                pendingRouteCallbacks.proceed = null;
                pendingRouteCallbacks.cancel = null;
            }
        }
        const handleLeave = () => {
            showUnsavedChangesModal.value = false;
            resetLogoState();
            resetToOriginal();
            proceedPendingNavigation();
        };
        // Exit modal's primary "Enregistrer les modifications": persist first, and only
        // resume the interrupted navigation once the save succeeds. On failure handleSave
        // has already surfaced the error and the modal stays open for a retry.
        async function handleSaveAndContinue() {
            if (!(await handleSave()))
                return;
            showUnsavedChangesModal.value = false;
            proceedPendingNavigation();
        }
        // ─── Pager navigation ─────────────────────────────────────────────────────────
        function goBack() {
            router.push(backLinkTo.value).catch(handleNavigationFailure("Organisation:back"));
        }
        function navigateToOrg(id) {
            return router.push({ name: "Organisation", params: { id } });
        }
        const pagerActions = computed(() => [
            {
                icon: "ri-arrow-left-line",
                label: t("pagination.previous"),
                ariaDisabled: isPreviousDisabled.value || isFetchingPrevious.value,
            },
            {
                icon: "ri-arrow-right-line",
                label: t("pagination.next"),
                ariaDisabled: isNextDisabled.value || isFetchingNext.value,
            },
        ]);
        // Index 0 = previous, 1 = next (order of `pagerActions`).
        function handlePagerAction(_action, index) {
            if (index === 0)
                goToPrevious(navigateToOrg);
            else
                goToNext(navigateToOrg);
        }
        // ─── Channel live-save ────────────────────────────────────────────────────────
        function reloadAfterChannelSave() {
            void loadEntity(organisationId.value);
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createElementVNode("div", _hoisted_1, [
                    (_unref(isLoading) && !state.id)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_2, _toDisplayString(_ctx.$t("common.loading")), 1 /* TEXT */))
                        : (state.id)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_3, _toDisplayString(_ctx.$t("organisation.detail.loaded", { name: state.mainInformation.legalName })), 1 /* TEXT */))
                            : _createCommentVNode("v-if", true),
                    (srAnnouncement.value)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_4, _toDisplayString(srAnnouncement.value), 1 /* TEXT */))
                        : _createCommentVNode("v-if", true)
                ]),
                (_unref(isLoading) && !state.id)
                    ? (_openBlock(), _createBlock(OrganisationDetailSkeleton, { key: 0 }))
                    : _createCommentVNode("v-if", true),
                (state.id)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                        _createVNode(_unref(NavHeader), {
                            type: "record",
                            title: state.mainInformation.legalName,
                            back: { label: backLinkLabel.value, href: _unref(router).resolve(backLinkTo.value).href },
                            avatar: {
                                name: state.mainInformation.legalName,
                                img: state.avatarUrl ?? undefined,
                                entity: 'organisation',
                                editLabel: _ctx.$t('organisation.avatar.editButton'),
                                editDisabledReason: _unref(canEdit) ? undefined : _unref(disabledTooltip),
                            },
                            badges: relationTypeBadges.value,
                            "secondary-action": { label: _ctx.$t('organisation.detail.actions.label'), menu: actionsMenuItems.value },
                            "icon-actions": pagerActions.value,
                            onBack: goBack,
                            onAvatarEdit: _cache[0] || (_cache[0] = ($event) => (logoUploadRef.value?.open())),
                            onIconAction: handlePagerAction
                        }, null, 8 /* PROPS */, ["title", "back", "avatar", "badges", "secondary-action", "icon-actions"]),
                        _createVNode(_unref(ImageUploadInput), {
                            ref_key: "logoUploadRef",
                            ref: logoUploadRef,
                            class: "hidden",
                            "avatar-size": 56,
                            "image-url": state.avatarUrl ?? undefined,
                            "image-alt": _ctx.$t('organisation.avatar.imageAlt'),
                            "edit-button-label": _ctx.$t('organisation.avatar.editButton'),
                            "modal-title": _ctx.$t('organisation.avatar.modalTitle'),
                            "cancel-button-label": _ctx.$t('organisation.avatar.cancelButton'),
                            "upload-button-label": _ctx.$t('organisation.avatar.uploadButton'),
                            "remove-button-label": _ctx.$t('organisation.avatar.removeButton'),
                            "browse-button-text": _ctx.$t('organisation.avatar.browseButton'),
                            placeholder: _ctx.$t('organisation.avatar.placeholder'),
                            "max-file-size-text": _ctx.$t('organisation.avatar.maxFileSizeText'),
                            "show-alert": false,
                            disabled: !_unref(canEdit),
                            "data-testid": "org-logo-upload",
                            onUpload: _unref(handleLogoUpload),
                            onRemove: _unref(handleLogoRemove),
                            onCancel: _unref(handleLogoCancel)
                        }, null, 8 /* PROPS */, ["image-url", "image-alt", "edit-button-label", "modal-title", "cancel-button-label", "upload-button-label", "remove-button-label", "browse-button-text", "placeholder", "max-file-size-text", "disabled", "onUpload", "onRemove", "onCancel"]),
                        _createElementVNode("div", _hoisted_6, [
                            _createElementVNode("div", _hoisted_7, [
                                _createVNode(_unref(ButtonGroup), {
                                    "model-value": activeTab.value,
                                    "panel-id": _unref(panelId),
                                    "overflow-label": _ctx.$t('common.tabs.next'),
                                    "overflow-previous-label": _ctx.$t('common.tabs.previous'),
                                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => (handleTabChange($event)))
                                }, {
                                    default: _withCtx(() => [
                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tabs.value, (tab) => {
                                            return (_openBlock(), _createBlock(_unref(Button), {
                                                key: tab.key,
                                                variant: "tertiary",
                                                size: "sm",
                                                label: tab.label,
                                                value: tab.key,
                                                "data-testid": `tab-${tab.key}`
                                            }, null, 8 /* PROPS */, ["label", "value", "data-testid"]));
                                        }), 128 /* KEYED_FRAGMENT */))
                                    ]),
                                    _: 1 /* STABLE */
                                }, 8 /* PROPS */, ["model-value", "panel-id", "overflow-label", "overflow-previous-label"])
                            ]),
                            _createVNode(_unref(FormSaveButton), {
                                enabled: _unref(canEdit) && isFormDirty.value && !hasClientValidationErrors.value,
                                saving: _unref(isSaving),
                                "save-label": _ctx.$t('form.saveChanges'),
                                "saving-label": _ctx.$t('form.saving'),
                                "disabled-hint": _ctx.$t('form.saveDisabledHint'),
                                "data-testid": "org-save",
                                onSave: handleSave
                            }, null, 8 /* PROPS */, ["enabled", "saving", "save-label", "saving-label", "disabled-hint"])
                        ]),
                        _createElementVNode("div", {
                            id: _unref(panelId),
                            role: "tabpanel",
                            "aria-labelledby": _unref(buttonGroupTabId)(_unref(panelId), activeTab.value),
                            class: "flex flex-col flex-1 min-h-0"
                        }, [
                            (activeTab.value === 'details')
                                ? (_openBlock(), _createBlock(OrganisationTabDetails, {
                                    key: 0,
                                    ref_key: "tabDetailsRef",
                                    ref: tabDetailsRef,
                                    "organisation-id": organisationId.value,
                                    state: state,
                                    addresses: _unref(organisationStore).addresses,
                                    "entity-layout": _unref(entityLayout),
                                    "layout-headers": layoutHeaders.value,
                                    "form-is-dirty": isFormDirty.value,
                                    onChannelSaved: reloadAfterChannelSave
                                }, null, 8 /* PROPS */, ["organisation-id", "state", "addresses", "entity-layout", "layout-headers", "form-is-dirty"]))
                                : (activeTab.value === 'contacts')
                                    ? (_openBlock(), _createBlock(OrganisationContactsTab, {
                                        key: 1,
                                        "organisation-id": organisationId.value,
                                        "organisation-name": state.mainInformation.legalName
                                    }, null, 8 /* PROPS */, ["organisation-id", "organisation-name"]))
                                    : (activeTab.value === 'donations')
                                        ? (_openBlock(), _createBlock(OrganisationDonationsTab, {
                                            key: 2,
                                            "organisation-id": organisationId.value,
                                            "organisation-name": state.mainInformation.legalName
                                        }, null, 8 /* PROPS */, ["organisation-id", "organisation-name"]))
                                        : (activeTab.value === 'subscriptions')
                                            ? (_openBlock(), _createBlock(OrganisationSubscriptionsTab, {
                                                key: 3,
                                                "organisation-id": organisationId.value,
                                                "organisation-name": state.mainInformation.legalName
                                            }, null, 8 /* PROPS */, ["organisation-id", "organisation-name"]))
                                            : _createCommentVNode("v-if", true)
                        ], 8 /* PROPS */, _hoisted_8)
                    ]))
                    : _createCommentVNode("v-if", true),
                _createVNode(_unref(UnsavedChangesModal), {
                    modelValue: showUnsavedChangesModal.value,
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((showUnsavedChangesModal).value = $event)),
                    title: _ctx.$t('leaveWithoutSaving.title'),
                    message: _ctx.$t('leaveWithoutSaving.message'),
                    "stay-label": _ctx.$t('leaveWithoutSaving.stay'),
                    "leave-label": _ctx.$t('leaveWithoutSaving.leave'),
                    "save-label": _ctx.$t('leaveWithoutSaving.save'),
                    "save-disabled": _unref(isSaving) || hasClientValidationErrors.value,
                    onDiscard: handleLeave,
                    onSave: handleSaveAndContinue,
                    onStay: handleStay
                }, null, 8 /* PROPS */, ["modelValue", "title", "message", "stay-label", "leave-label", "save-label", "save-disabled"]),
                _createVNode(OrganisationPropertiesModal, {
                    modelValue: showPropertiesModal.value,
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((showPropertiesModal).value = $event)),
                    properties: state.properties,
                    "legal-name": state.mainInformation.legalName
                }, null, 8 /* PROPS */, ["modelValue", "properties", "legal-name"])
            ], 64 /* STABLE_FRAGMENT */));
        };
    }
});
